import beTwMerge from '@/utils/beTwMerge';
import { ColorKeys } from '@/styles/colors';
import ResponsiveImage, { ResponsiveImageLoading, ResponsiveImageSet } from '@/utils/ResponsiveImage';
import Button from '../homepage/Button';
import RichText from '../RichText/RichText';

interface IAction {
  label: string;
  href: string;
}
interface ITile {
  imgSrcs?: ResponsiveImageSet;
  imgAlt?: string;
  imgLoad?: ResponsiveImageLoading;
  isRichtext?: boolean;
  optionalHeader?: string;
  optionalBody?: string;
  optionalLinkLabel?: string;
  optionalLinkUrl?: string;
  optionalLinkColor?: string;
  optionalLinkType?: 'primary' | 'secondary' | 'link';
  optionalSecondLinkLabel?: string;
  optionalSecondLinkUrl?: string;
  actions?: IAction[];
}

interface ITilesProps {
  tiles: ITile[];
  textPosition?: 'left' | 'center';
  type: StatSectionTypes;
  optionalLinkPositionLeft?: boolean;
  isTabletAsDesktop?: boolean;
  tileSeparatorColor?: ColorKeys;
  showMbTileSeparator?: boolean;
  textColor?: string;
  subTitleLines?: number;
  tileSubHeaderClass?: string;
}

export type StatSectionTypes = 'oneByFive' | 'oneByFour' | 'twoByTwo' | 'oneByThree';

interface IStatSectionProps {
  tiles: ITile[];
  classNames?: string;
  textPosition?: 'left' | 'center';
  header?: string;
  optionalLinkPositionLeft?: boolean;
  body?: string;
  type: StatSectionTypes;
  headerType?: 'text' | 'html';
  subTitleFontClass?: string;
  tileSeparatorColor?: ColorKeys;
  showMbTileSeparator?: boolean;
  color?: ColorKeys;
  isTabletAsDesktop?: boolean;
  action?: null | { label: string; type?: 'secondary' | 'primary' | 'link'; href: string; color?: ColorKeys };
  subTitleLines?: number;
  additionalClass?: string;
  containerClass?: string;
  tileSubHeaderClass?: string;
}

const typeStyles = {
  oneByFive: {
    tileContainer: 'tw-col-span-2 md:tw-col-span-1 lg:tw-col-span-1',
    imgContainer: 'tw-aspect-[358/218]',
  },
  oneByFour: {
    tileContainer: 'tw-col-span-2 md:tw-col-span-3 lg:tw-col-span-3',
    imgContainer: 'tw-aspect-[242/164]',
  },
  oneByThree: {
    tileContainer: 'tw-col-span-2 md:tw-col-span-3 lg:tw-col-span-4',
    imgContainer: 'tw-aspect-[358/218]',
  },
  twoByTwo: {
    tileContainer: 'tw-col-span-2 md:tw-col-span-3 lg:tw-col-span-6',
    imgContainer: 'tw-aspect-[559/300]',
  },
};

function Tiles({
  tiles,
  textPosition = 'center',
  type,
  optionalLinkPositionLeft = false,
  isTabletAsDesktop = false,
  tileSeparatorColor = 'white',
  showMbTileSeparator = false,
  textColor = 'tw-text-color-black',
  subTitleLines = 1,
  tileSubHeaderClass,
}: ITilesProps) {
  const isCenterText = textPosition === 'center';

  const separatorColor = {
    'light-green-light': 'tw-bg-color-light-green-light',
    'light-green': 'tw-bg-color-light-green',
    'dark-rose': 'tw-bg-color-dark-rose',
    transparent: 'tw-bg-color-transparent',
  };
  return (
    <div
      className={beTwMerge(
        'tw-col-span-full tw-grid tw-gap-y-8 tw-gap-x-2 md:tw-gap-x-6 lg:tw-gap-x-12 tw-grid-cols-[subgrid]',
        tiles.length === 5 && 'md:tw-grid-cols-5',
        isTabletAsDesktop && 'md:tw-grid-cols-12'
      )}
    >
      {tiles.slice(0, 5).map((tile, idx) => (
        <div
          key={`statSection-tile-${idx}`}
          className={beTwMerge(
            'tw-relative tw-flex tw-flex-col tw-gap-4',
            typeStyles[type]['tileContainer'],
            !tile.imgSrcs && 'tw-text-center lg:tw-text-left',
            type === 'oneByThree' ? 'lg:tw-px-0' : '',
            type === 'oneByFive' ? 'tw-px-0' : '',
            isTabletAsDesktop && 'md:tw-col-span-3'
          )}
        >
          {tile.imgSrcs && (
            <div className={beTwMerge(typeStyles[type]['imgContainer'])}>
              <ResponsiveImage
                sources={tile.imgSrcs}
                alt={tile.imgAlt || tile.optionalHeader}
                loading={tile.imgLoad}
                className="tw-w-full tw-object-cover tw-h-full"
              />
            </div>
          )}
          <div>
            {tile.optionalHeader && (
              <h3
                style={{ height: `${subTitleLines * 30}px` }}
                className={beTwMerge(
                  'tw-w-full tw-mb-2 lg:tw-mb-4 tw-mt-0 tw-inline-block h4b max-[1024px]:!tw-h-auto',
                  isCenterText && 'tw-text-center',
                  isTabletAsDesktop && 'md:tw-block md:!tw-h-[90px]',
                  tileSubHeaderClass
                )}
              >
                {tile.optionalHeader}
              </h3>
            )}
            {tile.isRichtext ? (
              <RichText
                className={beTwMerge(
                  'tw-mb-0 tw-text-color-black  [&_p]:tw-font-roman [&>p]:!tw-leading-[1.4em] md:[&>p]:!tw-text-[14px] md:[&>p>a]:!tw-text-[14px] lg:[&>p]:!tw-text-[16px] lg:[&>p>a]:!tw-text-[16px]',
                  isCenterText ? 'tw-text-center' : ''
                )}
                content={tile.optionalBody || ''}
              />
            ) : (
              <p
                className={beTwMerge('tw-mb-0 tw-leading-[1.4em] tw-font-roman', isCenterText ? 'tw-text-center' : '')}
              >
                {tile.optionalBody}
              </p>
            )}
          </div>
          {tile.optionalLinkLabel && (
            <div
              className={beTwMerge(
                'tw-flex tw-flex-col tw-gap-[16px] large:tw-gap-[24px] tw-text-sm',
                isCenterText && 'tw-items-center lg:tw-items-center'
              )}
            >
              <Button
                className={beTwMerge(
                  'tw-max-w-[768px]',
                  !isCenterText && 'tw-text-left',
                  isTabletAsDesktop && 'md:tw-items-start'
                )}
                label={tile.optionalLinkLabel}
                ariaLabel={`${tile.optionalLinkLabel} on ${tile.optionalHeader}`}
                color={(tile.optionalLinkColor as ColorKeys) || 'white'}
                type={tile.optionalLinkType || 'link'}
                href={tile.optionalLinkUrl}
              />
              {tile?.actions?.map((action, index) => (
                <a
                  key={index}
                  className={beTwMerge('tw-underline hover:tw-text-color-light-green-light', textColor)}
                  href={action.href}
                >
                  {action.label}
                </a>
              ))}
            </div>
          )}
          {tile.optionalSecondLinkLabel && (
            <div
              className={beTwMerge(
                'tw-flex tw-flex-col tw-gap-[16px] large:tw-gap-[24px]',
                optionalLinkPositionLeft && 'tw-items-center lg:tw-items-start',
                isTabletAsDesktop && 'md:tw-items-start'
              )}
            >
              <a
                className={beTwMerge(
                  'tw-underline hover:tw-text-color-light-green-light tw-w-fit specialLink',
                  textColor
                )}
                href={tile.optionalSecondLinkUrl}
              >
                {tile.optionalSecondLinkLabel}
              </a>
            </div>
          )}
          {type === 'oneByFive' && idx !== 4 && (
            <div
              className={beTwMerge(
                'lg:tw-absolute md:tw-hidden lg:tw-block lg:tw-top-0 tw-bottom-0 lg:tw-right-[-24px] lg:tw-w-[1.5px] lg:tw-h-auto tw-bg-color-white tw-h-[1.5px]',
                separatorColor[tileSeparatorColor],
                isTabletAsDesktop && 'md:tw-block'
              )}
            />
          )}
          {type === 'oneByFour' && idx !== 3 && (
            <div
              className={beTwMerge(
                'lg:tw-absolute lg:tw-top-0 tw-bottom-0 lg:tw-right-[-24px] lg:tw-w-[1.5px] lg:tw-h-auto tw-bg-color-white tw-h-[1.5px] tw-hidden lg:tw-block',
                separatorColor[tileSeparatorColor],
                showMbTileSeparator && 'tw-block'
              )}
            />
          )}
          {type === 'oneByThree' && idx !== 2 && (
            <div
              className={beTwMerge(
                'lg:tw-absolute lg:tw-top-0 tw-bottom-0 lg:tw-right-[-24px] lg:tw-w-[1.5px] lg:tw-h-auto tw-bg-color-white tw-h-[1.5px] tw-hidden lg:tw-block',
                separatorColor[tileSeparatorColor],
                showMbTileSeparator && 'tw-block'
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default function StatSection({
  header,
  tiles,
  textPosition = 'center',
  body,
  type,
  action,
  classNames,
  optionalLinkPositionLeft = false,
  isTabletAsDesktop = false,
  tileSeparatorColor,
  showMbTileSeparator = false,
  color = 'white',
  subTitleLines = 1,
  additionalClass = '',
  headerType = 'text',
  subTitleFontClass = '',
  containerClass = '',
  tileSubHeaderClass = '',
}: IStatSectionProps) {
  const backgroundColor = {
    'light-green-light': 'tw-bg-color-light-green-light tw-text-white',
    'light-green': 'tw-bg-color-light-green tw-text-white',
    'dark-rose-light': 'tw-bg-color-dark-rose-light tw-text-color-black',
    'dark-rose': 'tw-bg-color-dark-rose tw-text-color-black',
    'dark-green': 'tw-bg-color-dark-green tw-text-white',
    cream: 'tw-bg-color-cream tw-text-color-black',
    white: 'tw-bg-white tw-text-color-black',
    'dark-green-dark': 'tw-bg-color-dark-green-dark tw-text-white',
    'white-grey-20': 'tw-bg-color-grey-20',
    'mbBlack-dtWhite': 'tw-bg-white tw-text-color-black lg:tw-bg-color-dark-green lg:tw-text-white',
  };
  const textColor = backgroundColor[color].includes('tw-text-white')
    ? 'tw-text-white hover:tw-text-be-dark-gray'
    : 'tw-text-color-black';

  const moreDescriptiveAriaLabel = (
    headerTitle: string | undefined,
    href: string | undefined,
    label: string
  ): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (headerTitle) {
        return `${headerTitle} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')} page`;
    }
    return label || '';
  };
  return (
    <div
      className={beTwMerge(
        'tw-w-full tw-flex tw-justify-center tw-relative tw-pt-[56px] lg:tw-pt-[80px]',
        backgroundColor[color],
        classNames,
        additionalClass
      )}
    >
      <div
        className={beTwMerge(
          'tw-gap-y-8 lg:tw-gap-y-[56px] tw-gap-x-2 md:tw-gap-x-6 lg:tw-gap-x-12 tw-grid tw-grid-cols-2 md:tw-grid-cols-6 lg:tw-grid-cols-12 tw-max-w-[1206px] tw-px-4 lg:tw-px-8 tw-mb-14 lg:tw-mb-20',
          containerClass
        )}
      >
        {(header || body) && (
          <div className="tw-col-span-full tw-flex tw-flex-col tw-gap-6 tw-max-w-[910px] tw-mx-auto">
            {header && headerType === 'text' && (
              <h2 className="tw-mb-0 tw-text-center !tw-text-[22px] lg:!tw-text-[32px] h2b">{header}</h2>
            )}
            {header && headerType === 'html' && (
              <h2
                className="tw-mb-0 tw-text-center !tw-text-[22px] lg:!tw-text-[32px] h2b"
                dangerouslySetInnerHTML={{ __html: header }}
              />
            )}
            {body && (
              <p className={beTwMerge('tw-max-w-[910px] tw-mx-auto tw-text-center h3', subTitleFontClass)}>{body}</p>
            )}
          </div>
        )}

        <Tiles
          tiles={tiles}
          textPosition={textPosition}
          type={type}
          tileSeparatorColor={tileSeparatorColor}
          showMbTileSeparator={showMbTileSeparator}
          textColor={textColor}
          subTitleLines={subTitleLines}
          isTabletAsDesktop={isTabletAsDesktop}
          optionalLinkPositionLeft={optionalLinkPositionLeft}
          tileSubHeaderClass={tileSubHeaderClass}
        />
        {action?.label && (
          <div className="tw-col-span-full tw-flex tw-justify-center">
            <Button
              ariaLabel={moreDescriptiveAriaLabel(header, action.href, action.label)}
              label={action.label}
              color={action.color}
              type={action.type || 'secondary'}
              href={action.href}
            />
          </div>
        )}
      </div>
    </div>
  );
}
